/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { cssMap as nativeCssMap } from '@compiled/react';
import isNil from 'lodash/isNil';
import { ErrorBoundary } from 'react-error-boundary';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import Button from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import { Anchor, Box, Flex, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';

import { ActionCardWrapper } from '../../common/ui/action-card-wrapper';

import { BoardQuery, type HydratedBoard } from './BoardQuery';
import {
	filterActionsByIndexWithHydration,
	getIdsForHydration,
	isActionCompleted,
} from './capacity-planning-utils';
import { CapacityPlanningDataQuery } from './CapacityPlanningDataQuery';
import { CapacityPlanningCardFooter } from './footer';
import { PeopleView } from './people-view/PeopleView';
import { SuccessMessage } from './SuccessMessage';
import { type HydratedUser, type HydratedWork, VIEW_TYPE } from './types';
import { WorkView } from './work-view/WorkView';

const actionCardTitleMessage = defineMessage({
	id: 'capacity-planning.action-card-title.non-final',
	defaultMessage: 'Manage capacity plan',
	description: 'Title displayed in the header of the capacity planning people view rovo chat card.',
});
const subtitleMessage = defineMessage({
	id: 'capacity-planning.updated-in-board.non-final',
	defaultMessage: 'Updated in {boardLink}',
	description:
		'Subtitle when scope (board/project) is known: "Updated in" followed by a clickable board/project name link.',
});
const unableToProcessMessage = defineMessage({
	id: 'capacity-planning.unable-to-process.non-final',
	defaultMessage: 'We were unable to process your request. Please try again later.',
	description: 'Message shown when there are no actions to display (e.g. empty or failed payload).',
});
const showMoreMessage = defineMessage({
	id: 'capacity-planning.show-more.non-final',
	defaultMessage: 'Show more',
	description: 'Button label for expanding the capacity planning card content to show all actions.',
});

const ANIMATION_DURATION = '350ms';
const MAX_CONTENT_HEIGHT = '320px';

const contentStyles = cssMap({
	collapsible: {
		maxHeight: MAX_CONTENT_HEIGHT,
		overflow: 'clip',
		position: 'relative',
		transition: 'max-height',
		transitionTimingFunction: 'ease-in-out',
		transitionDuration: ANIMATION_DURATION,
	},
	expanded: {
		maxHeight: 'max-content',
	},
	gradientSkrim: {
		position: 'absolute',
		bottom: 0,
		left: 0,
		right: 0,
		height: '60px',
		zIndex: 100,
		transition: 'opacity',
		transitionTimingFunction: 'ease-in-out',
		transitionDuration: ANIMATION_DURATION,
	},
	showMoreButtonContainer: {
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.full'),
		// @ts-expect-error - compound boxShadow using ds-shadow tokens not in cssMap union type; required for elevation design
		boxShadow: `0px 16px 24px 0px ${token('elevation.shadow.overflow.spread')}, 0px 0px 2px 0px ${token('elevation.shadow.overflow.perimeter')}`,
		transition: 'opacity',
		transitionTimingFunction: 'ease-in-out',
		transitionDuration: ANIMATION_DURATION,
	},
});

const nativeContentStyles = nativeCssMap({
	collapsible: {
		interpolateSize: 'allow-keywords',
	},
	gradientSkrim: {
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- there is no token for gradient skrims
		backgroundImage: 'linear-gradient(to top, var(--ds-surface) 0%, transparent 100%)',
	},
});

function getScopeId(
	action: ActionRendererFor<typeof ActionKeys.ManageCapacityPlan>['actions'][number],
): string {
	const data = action.data;
	return data.capacityPlanContext.scopeId;
}

function Subtitle({ board }: { board: HydratedBoard }): React.ReactNode {
	return (
		<FormattedMessage
			{...subtitleMessage}
			values={{
				boardLink: board.url ? (
					<Anchor href={board.url} target="_blank" rel="noopener noreferrer">
						{board.name}
					</Anchor>
				) : (
					board.name
				),
			}}
		/>
	);
}

function UnableToProcess({ viewType }: { viewType?: string }) {
	di(useAnalytics);
	const { sendAnalyticsEvent } = useAnalytics();

	useEffect(() => {
		sendAnalyticsEvent({
			action: 'unableToProcess',
			actionSubject: 'component',
			actionSubjectId: 'capacityPlanningPreviewCard',
			eventType: 'ui',
			attributes: {
				viewType,
			},
		});
	}, [sendAnalyticsEvent, viewType]);

	return (
		<Box>
			<Text size="medium">
				<FormattedMessage {...unableToProcessMessage} />
			</Text>
		</Box>
	);
}

const skeleton = <Skeleton width="100%" height="320px" />;

export const CapacityPlanningActionRenderer = (
	props: ActionRendererFor<typeof ActionKeys.ManageCapacityPlan> & { expandAllGroups?: boolean },
): JSX.Element => {
	const ids = useMemo(
		() => getIdsForHydration(props.actions.filter((a) => a.status !== 'cancelled')),
		[props.actions],
	);

	if (props.actions.length === 0) {
		return <UnableToProcess />;
	}

	return (
		<ErrorBoundary fallbackRender={() => <UnableToProcess />}>
			<Suspense fallback={skeleton}>
				<BoardQuery scopeId={getScopeId(props.actions[0])}>
					{(board) =>
						!board?.name ? (
							<UnableToProcess />
						) : (
							<CapacityPlanningDataQuery contributorIds={ids.contributorIds} workIds={ids.workIds}>
								{({ usersByAccountId, workById }) => (
									<CapacityPlanningContent
										{...props}
										subtitle={<Subtitle board={board} />}
										usersByAccountId={usersByAccountId}
										workById={workById}
										expandAllGroups={props.expandAllGroups}
									/>
								)}
							</CapacityPlanningDataQuery>
						)
					}
				</BoardQuery>
			</Suspense>
		</ErrorBoundary>
	);
};

function CollapsibleContent({ children }: { children: React.ReactNode }): React.JSX.Element {
	const { formatMessage } = useIntl();
	const contentRef = useRef<HTMLDivElement>(null);
	const [isExpanded, setIsExpanded] = useState(false);
	const [isOverflowed, setIsOverflowed] = useState(true);

	useEffect(() => {
		const content = contentRef.current;
		if (!content || isExpanded) return;

		const checkOverflow = () => setIsOverflowed(content.scrollHeight > content.clientHeight);
		checkOverflow();
		const observer = new ResizeObserver(checkOverflow);
		observer.observe(content);
		return () => observer.disconnect();
	}, [isExpanded]);

	return (
		<Box
			ref={contentRef}
			xcss={cx(
				contentStyles.collapsible,
				isExpanded && contentStyles.expanded,
				nativeContentStyles.collapsible,
			)}
			testId="capacity-planning-collapsible-content"
		>
			{children}
			{!isExpanded && isOverflowed && (
				<Flex
					alignItems="center"
					justifyContent="center"
					xcss={cx(contentStyles.gradientSkrim, nativeContentStyles.gradientSkrim)}
				>
					<Flex
						alignItems="center"
						justifyContent="center"
						xcss={contentStyles.showMoreButtonContainer}
					>
						<Button
							appearance="subtle"
							onClick={() => {
								setIsExpanded(true);
							}}
							testId="capacity-planning-show-more-button"
						>
							{formatMessage(showMoreMessage)}
						</Button>
					</Flex>
				</Flex>
			)}
		</Box>
	);
}

type CapacityPlanningContentProps = ActionRendererFor<typeof ActionKeys.ManageCapacityPlan> & {
	subtitle: React.ReactNode;
	usersByAccountId: Map<string, HydratedUser>;
	workById: Map<string, HydratedWork>;
	expandAllGroups?: boolean;
};

function CapacityPlanningContent({
	actions,
	subtitle,
	usersByAccountId,
	workById,
	onConfirm,
	onCancel,
	getStatus,
	expandAllGroups = false,
}: CapacityPlanningContentProps) {
	di(useAnalytics);
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

	const validNonCancelledActions = useMemo(
		() =>
			actions.filter(
				(action) =>
					!(
						action.status === 'cancelled' ||
						getStatus(action) === 'cancelled' ||
						isNil(action.data)
					),
			),
		[actions, getStatus],
	);

	const isPeopleView = useMemo(() => {
		return !validNonCancelledActions.some(
			(action) => action?.data?.capacityPlanContext?.view === VIEW_TYPE.WORK,
		);
	}, [validNonCancelledActions]);

	const viewType = isPeopleView ? VIEW_TYPE.PEOPLE : VIEW_TYPE.WORK;

	const hasRenderedAnalyticsFired = useRef(false);
	useEffect(() => {
		if (hasRenderedAnalyticsFired.current) {
			return;
		}
		hasRenderedAnalyticsFired.current = true;
		sendAnalyticsEvent({
			action: 'rendered',
			actionSubject: 'component',
			actionSubjectId: 'capacityPlanningPreviewCard',
			eventType: 'ui',
			attributes: {
				viewType,
				actionsCount: validNonCancelledActions.length,
			},
		});
	}, [sendAnalyticsEvent, viewType, validNonCancelledActions.length]);

	const { actionsByContributorId, actionsByWorkId } = useMemo(
		() =>
			filterActionsByIndexWithHydration(
				validNonCancelledActions,
				usersByAccountId,
				workById,
				isPeopleView ? VIEW_TYPE.PEOPLE : VIEW_TYPE.WORK,
			),
		[validNonCancelledActions, usersByAccountId, workById, isPeopleView],
	);

	const actionsByIndex = useMemo(
		() => ({ actionsByContributorId, actionsByWorkId }),
		[actionsByContributorId, actionsByWorkId],
	);

	const displayedActions = useMemo(() => {
		if (isPeopleView) {
			return Array.from(actionsByContributorId.values()).flat();
		}
		return Array.from(actionsByWorkId.values()).flat();
	}, [actionsByContributorId, actionsByWorkId, isPeopleView]);

	const handleSelectionChange = useCallback((invocationId: string, isSelected: boolean) => {
		setSelectedKeys((prevSelectedKeys) => {
			const updatedKeys = new Set(prevSelectedKeys);
			isSelected ? updatedKeys.add(invocationId) : updatedKeys.delete(invocationId);
			return updatedKeys;
		});
	}, []);

	const clearSelection = useCallback(() => setSelectedKeys(new Set()), []);

	// If there are no renderable actions, determine whether to show success or error
	if (displayedActions.length === 0) {
		// User has interacted with at least one action — show success capsule
		const userHasInteracted = actions.some((action) => isActionCompleted(action, getStatus));
		return userHasInteracted ? (
			<SuccessMessage actions={actions} getStatus={getStatus} />
		) : (
			<UnableToProcess viewType={viewType} />
		);
	}

	const allActionsApplied = displayedActions.every((action) =>
		isActionCompleted(action, getStatus),
	);

	return (
		<Box>
			<ActionCardWrapper
				title={formatMessage(actionCardTitleMessage)}
				subtitle={subtitle}
				product="capacity-planning"
				isExpandable
				defaultExpanded={true}
			>
				<CollapsibleContent>
					{isPeopleView ? (
						<PeopleView
							actionsByIndex={actionsByIndex}
							hydratedUsers={usersByAccountId}
							workById={workById}
							selectedKeys={selectedKeys}
							onSelectionChange={handleSelectionChange}
							onConfirm={onConfirm}
							onCancel={onCancel}
							getStatus={getStatus}
						/>
					) : (
						<WorkView
							actionsByIndex={actionsByIndex}
							hydratedUsers={usersByAccountId}
							workById={workById}
							selectedKeys={selectedKeys}
							onSelectionChange={handleSelectionChange}
							onConfirm={onConfirm}
							onCancel={onCancel}
							getStatus={getStatus}
							expandAllGroups={expandAllGroups}
						/>
					)}
				</CollapsibleContent>
				<CapacityPlanningCardFooter
					displayedActions={displayedActions}
					selectedKeys={selectedKeys}
					getStatus={getStatus}
					onConfirm={onConfirm}
					onCancel={onCancel}
					onClearSelection={clearSelection}
					allActionsApplied={allActionsApplied}
					viewType={viewType}
				/>
			</ActionCardWrapper>
			{allActionsApplied && <SuccessMessage actions={displayedActions} getStatus={getStatus} />}
		</Box>
	);
}
